import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const empty = {
  label: "", color: "bw", sides: "single", size: "A4", paper: "normal",
  min_qty: 1, max_qty: 5, unit_price: 0.1,
};

export default function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [prices, setPrices] = useState([]);
  const [newTier, setNewTier] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [clientTags, setClientTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [loyaltyLevels, setLoyaltyLevels] = useState([]);
  const [newPeriod, setNewPeriod] = useState({
    name: "",
    start_date: "",
    end_date: "",
    message: "",
    show_notice: true,
    block_orders: false,
    block_pickups: true,
    active: true,
  });

  const load = async () => {
    const [s, p, tags, levels] = await Promise.all([
      api.get("/settings"),
      api.get("/admin/prices"),
      api.get("/admin/client-tags"),
      api.get("/admin/loyalty-levels"),
    ]);
    setSettings(s.data || {});
    setPrices(p.data || []);
    setClientTags(tags.data?.tags || []);
    setLoyaltyLevels(levels.data?.levels || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", settings);
      toast.success("Configuración guardada");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const addTier = async () => {
    try {
      await api.post("/admin/prices", newTier);
      toast.success("Tarifa añadida");
      setNewTier(empty);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delTier = async (id) => {
    await api.delete(`/admin/prices/${id}`);
    toast.success("Tarifa eliminada");
    await load();
  };

  const saveTags = async (tags) => {
    try {
      const { data } = await api.put("/admin/client-tags", { tags });
      setClientTags(data.tags || []);
      toast.success("Etiquetas guardadas");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addClientTag = async () => {
    const tag = newTag.trim().toLowerCase().replaceAll(" ", "_");
    if (!tag) return;
    if (clientTags.includes(tag)) {
      toast.error("Esa etiqueta ya existe");
      return;
    }
    await saveTags([...clientTags, tag]);
    setNewTag("");
  };

  const removeClientTag = async (tag) => {
    await saveTags(clientTags.filter((t) => t !== tag));
  };

  const updateLevel = (idx, patch) => {
    setLoyaltyLevels((prev) => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
  };

  const saveLoyaltyLevels = async () => {
    try {
      const { data } = await api.put("/admin/loyalty-levels", { levels: loyaltyLevels });
      setLoyaltyLevels(data.levels || []);
      toast.success("Niveles KAPEI guardados");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updatePeriod = (idx, patch) => {
    const periods = settings.calendar_periods || [];
    setSettings({
      ...settings,
      calendar_periods: periods.map((p, i) => i === idx ? { ...p, ...patch } : p),
    });
  };

  const addCalendarPeriod = () => {
    if (!newPeriod.name.trim() || !newPeriod.start_date || !newPeriod.end_date) {
      toast.error("Nombre, fecha inicio y fecha fin son obligatorios");
      return;
    }
    const period = {
      ...newPeriod,
      id: `period_${Date.now()}`,
    };
    setSettings({
      ...settings,
      calendar_periods: [...(settings.calendar_periods || []), period],
    });
    setNewPeriod({
      name: "",
      start_date: "",
      end_date: "",
      message: "",
      show_notice: true,
      block_orders: false,
      block_pickups: true,
      active: true,
    });
  };

  const removeCalendarPeriod = (id) => {
    setSettings({
      ...settings,
      calendar_periods: (settings.calendar_periods || []).filter((p) => p.id !== id),
    });
  };

  const set = (k) => (e) => setSettings({ ...settings, [k]: e.target.value });

  return (
    <div className="max-w-4xl">
      <h1 className="mb-6 text-3xl font-black text-slate-900">Configuración</h1>

      <section className="mb-8 rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-extrabold">Datos generales</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>Teléfono</Label>
            <Input data-testid="cfg-phone" value={settings.contact_phone || ""} onChange={set("contact_phone")} className="mt-1" />
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="cfg-email" value={settings.contact_email || ""} onChange={set("contact_email")} className="mt-1" />
          </div>
          <div className="md:col-span-2">
            <Label>Dirección</Label>
            <Input data-testid="cfg-address" value={settings.address || ""} onChange={set("address")} className="mt-1" />
          </div>
          <div className="md:col-span-2">
            <Label>Horarios</Label>
            <Input data-testid="cfg-hours" value={settings.business_hours || ""} onChange={set("business_hours")} className="mt-1" />
          </div>
          <div>
            <Label>Días hasta borrar archivos</Label>
            <Input
              data-testid="cfg-autodelete"
              type="number" min={1}
              value={settings.auto_delete_days || 30}
              onChange={(e) => setSettings({ ...settings, auto_delete_days: parseInt(e.target.value || "30", 10) })}
              className="mt-1"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Texto de privacidad</Label>
            <Textarea
              data-testid="cfg-privacy"
              value={settings.privacy_text || ""} onChange={set("privacy_text")}
              className="mt-1 min-h-[120px]"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button data-testid="cfg-save" onClick={save} disabled={saving} className="bg-[#4d7c0f] hover:bg-[#3f6212]">
            {saving ? "Guardando…" : "Guardar configuración"}
          </Button>
        </div>
      </section>


      <section className="mb-8 rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-2 text-lg font-extrabold">Calendario KAPEI</h2>
        <p className="mb-4 text-sm text-slate-600">
          Añade vacaciones, festivos o bloqueos. Puedes bloquear pedidos completos o solo fechas de recogida.
        </p>

        <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <h3 className="mb-3 font-black text-slate-900">Añadir periodo</h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label>Nombre</Label>
              <Input value={newPeriod.name} onChange={(e) => setNewPeriod({ ...newPeriod, name: e.target.value })} placeholder="Vacaciones verano" className="mt-1" />
            </div>
            <div>
              <Label>Mensaje visible</Label>
              <Input value={newPeriod.message} onChange={(e) => setNewPeriod({ ...newPeriod, message: e.target.value })} placeholder="Cerrado por vacaciones..." className="mt-1" />
            </div>
            <div>
              <Label>Desde</Label>
              <Input type="date" value={newPeriod.start_date} onChange={(e) => setNewPeriod({ ...newPeriod, start_date: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Hasta</Label>
              <Input type="date" value={newPeriod.end_date} onChange={(e) => setNewPeriod({ ...newPeriod, end_date: e.target.value })} className="mt-1" />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-4">
            <label className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-bold">
              <input type="checkbox" checked={newPeriod.active} onChange={(e) => setNewPeriod({ ...newPeriod, active: e.target.checked })} />
              Activo
            </label>
            <label className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-bold">
              <input type="checkbox" checked={newPeriod.show_notice} onChange={(e) => setNewPeriod({ ...newPeriod, show_notice: e.target.checked })} />
              Mostrar aviso
            </label>
            <label className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-bold">
              <input type="checkbox" checked={newPeriod.block_orders} onChange={(e) => setNewPeriod({ ...newPeriod, block_orders: e.target.checked })} />
              Bloquear pedidos
            </label>
            <label className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-bold">
              <input type="checkbox" checked={newPeriod.block_pickups} onChange={(e) => setNewPeriod({ ...newPeriod, block_pickups: e.target.checked })} />
              Bloquear recogidas
            </label>
          </div>

          <div className="mt-4 flex justify-end">
            <Button type="button" onClick={addCalendarPeriod} variant="outline">
              Añadir periodo
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          {(settings.calendar_periods || []).map((period, idx) => (
            <div key={period.id || idx} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <div>
                  <Label>Nombre</Label>
                  <Input value={period.name || ""} onChange={(e) => updatePeriod(idx, { name: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Desde</Label>
                  <Input type="date" value={period.start_date || ""} onChange={(e) => updatePeriod(idx, { start_date: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Hasta</Label>
                  <Input type="date" value={period.end_date || ""} onChange={(e) => updatePeriod(idx, { end_date: e.target.value })} className="mt-1" />
                </div>
                <div className="flex items-end justify-end">
                  <Button type="button" variant="ghost" onClick={() => removeCalendarPeriod(period.id)}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
                <div className="md:col-span-4">
                  <Label>Mensaje</Label>
                  <Input value={period.message || ""} onChange={(e) => updatePeriod(idx, { message: e.target.value })} className="mt-1" />
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-4">
                <label className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm font-bold">
                  <input type="checkbox" checked={period.active !== false} onChange={(e) => updatePeriod(idx, { active: e.target.checked })} />
                  Activo
                </label>
                <label className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm font-bold">
                  <input type="checkbox" checked={period.show_notice !== false} onChange={(e) => updatePeriod(idx, { show_notice: e.target.checked })} />
                  Mostrar aviso
                </label>
                <label className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm font-bold">
                  <input type="checkbox" checked={!!period.block_orders} onChange={(e) => updatePeriod(idx, { block_orders: e.target.checked })} />
                  Bloquear pedidos
                </label>
                <label className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm font-bold">
                  <input type="checkbox" checked={period.block_pickups !== false} onChange={(e) => updatePeriod(idx, { block_pickups: e.target.checked })} />
                  Bloquear recogidas
                </label>
              </div>
            </div>
          ))}

          {(settings.calendar_periods || []).length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">
              No hay periodos configurados.
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <Button type="button" onClick={save} disabled={saving} className="bg-[#4d7c0f] hover:bg-[#3f6212]">
            Guardar calendario
          </Button>
        </div>
      </section>

      <section className="mb-8 rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-2 text-lg font-extrabold">Niveles KAPEI</h2>
        <p className="mb-4 text-sm text-slate-600">
          Configura cuándo suben los clientes de nivel. OR = sube si cumple pedidos o páginas. AND = debe cumplir ambas cosas. MANUAL = solo lo asignas tú.
        </p>

        <div className="space-y-3">
          {loyaltyLevels.map((level, idx) => (
            <div key={level.id || idx} className="grid grid-cols-1 gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 md:grid-cols-6">
              <div>
                <Label>Icono</Label>
                <Input value={level.icon || ""} onChange={(e) => updateLevel(idx, { icon: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Nombre</Label>
                <Input value={level.label || ""} onChange={(e) => updateLevel(idx, { label: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Pedidos</Label>
                <Input
                  type="number"
                  min={0}
                  value={level.min_orders || 0}
                  onChange={(e) => updateLevel(idx, { min_orders: parseInt(e.target.value || "0", 10) })}
                  className="mt-1"
                  disabled={level.mode === "MANUAL"}
                />
              </div>
              <div>
                <Label>Páginas</Label>
                <Input
                  type="number"
                  min={0}
                  value={level.min_pages || 0}
                  onChange={(e) => updateLevel(idx, { min_pages: parseInt(e.target.value || "0", 10) })}
                  className="mt-1"
                  disabled={level.mode === "MANUAL"}
                />
              </div>
              <div>
                <Label>Modo</Label>
                <Select value={level.mode || "OR"} onValueChange={(v) => updateLevel(idx, { mode: v, manual_only: v === "MANUAL" })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OR">OR</SelectItem>
                    <SelectItem value="AND">AND</SelectItem>
                    <SelectItem value="MANUAL">MANUAL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <div className="text-xs text-slate-500">
                  {level.mode === "AND" ? "Pedidos y páginas" : level.mode === "MANUAL" ? "Solo manual" : "Pedidos o páginas"}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex justify-end">
          <Button type="button" onClick={saveLoyaltyLevels} className="bg-[#4d7c0f] hover:bg-[#3f6212]">
            Guardar niveles
          </Button>
        </div>
      </section>

      <section className="mb-8 rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-2 text-lg font-extrabold">Etiquetas de clientes</h2>
        <p className="mb-4 text-sm text-slate-600">
          Crea etiquetas reutilizables para segmentar clientes y aplicar promociones sin errores de escritura.
        </p>

        <div className="mb-4 flex flex-col gap-2 md:flex-row">
          <Input
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder="Nueva etiqueta, por ejemplo: oposiciones_2026"
            className="md:max-w-md"
          />
          <Button type="button" onClick={addClientTag} className="bg-[#4d7c0f] hover:bg-[#3f6212]">
            Añadir etiqueta
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {clientTags.map((tag) => (
            <span key={tag} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-bold text-slate-700">
              {tag}
              <button
                type="button"
                onClick={() => removeClientTag(tag)}
                className="text-red-600 hover:text-red-800"
                title="Eliminar etiqueta"
              >
                ×
              </button>
            </span>
          ))}
          {clientTags.length === 0 && (
            <span className="text-sm italic text-slate-500">No hay etiquetas todavía.</span>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-extrabold">Tarifas (orientativas internas)</h2>

        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="p-2">Etiqueta</th>
                <th className="p-2">Tinta</th>
                <th className="p-2">Caras</th>
                <th className="p-2">Tamaño</th>
                <th className="p-2">Papel</th>
                <th className="p-2">Min</th>
                <th className="p-2">Max</th>
                <th className="p-2">€/u</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {prices.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="p-2 font-bold">{p.label}</td>
                  <td className="p-2">{p.color}</td>
                  <td className="p-2">{p.sides}</td>
                  <td className="p-2">{p.size}</td>
                  <td className="p-2">{p.paper}</td>
                  <td className="p-2">{p.min_qty}</td>
                  <td className="p-2">{p.max_qty}</td>
                  <td className="p-2 font-bold">{Number(p.unit_price).toFixed(2)}</td>
                  <td className="p-2 text-right">
                    <Button data-testid={`del-tier-${p.id}`} variant="ghost" size="sm" onClick={() => delTier(p.id)}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
              {prices.length === 0 && (
                <tr><td colSpan={9} className="p-4 text-center text-slate-500">Sin tarifas todavía.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-9">
          <Input
            data-testid="tier-label"
            placeholder="Etiqueta" value={newTier.label}
            onChange={(e) => setNewTier({ ...newTier, label: e.target.value })}
            className="md:col-span-2"
          />
          <Select value={newTier.color} onValueChange={(v) => setNewTier({ ...newTier, color: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="bw">B/N</SelectItem>
              <SelectItem value="color">Color</SelectItem>
            </SelectContent>
          </Select>
          <Select value={newTier.sides} onValueChange={(v) => setNewTier({ ...newTier, sides: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="single">1 cara</SelectItem>
              <SelectItem value="double">2 caras</SelectItem>
            </SelectContent>
          </Select>
          <Select value={newTier.size} onValueChange={(v) => setNewTier({ ...newTier, size: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="A4">A4</SelectItem>
              <SelectItem value="A3">A3</SelectItem>
              <SelectItem value="Otro">Otro</SelectItem>
            </SelectContent>
          </Select>
          <Select value={newTier.paper} onValueChange={(v) => setNewTier({ ...newTier, paper: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="cartulina">Cartulina</SelectItem>
              <SelectItem value="fotografico">Fotográfico</SelectItem>
              <SelectItem value="pegatina">Pegatina</SelectItem>
              <SelectItem value="otro">Otro</SelectItem>
            </SelectContent>
          </Select>
          <Input type="number" min={1} placeholder="Min" value={newTier.min_qty}
            onChange={(e) => setNewTier({ ...newTier, min_qty: parseInt(e.target.value || "1", 10) })} />
          <Input type="number" min={1} placeholder="Max" value={newTier.max_qty}
            onChange={(e) => setNewTier({ ...newTier, max_qty: parseInt(e.target.value || "1", 10) })} />
          <Input type="number" step="0.01" placeholder="€/u" value={newTier.unit_price}
            onChange={(e) => setNewTier({ ...newTier, unit_price: parseFloat(e.target.value || "0") })} />
        </div>
        <div className="mt-2 flex justify-end">
          <Button data-testid="add-tier" onClick={addTier} className="gap-2 bg-[#4d7c0f] hover:bg-[#3f6212]">
            <Plus className="h-4 w-4" /> Añadir tarifa
          </Button>
        </div>
      </section>
    </div>
  );
}
